import React, { useState, useEffect } from 'react';
import Loader from './Loader';

const TshirtModal = ({ tshirt, onAddToCart, onClose, cartItems, productType }) => {
  const [selectedSize, setSelectedSize] = useState(null);
  const [selectedColor, setSelectedColor] = useState(null);
  const [showSizeGuide, setShowSizeGuide] = useState(false);
  const [currentView, setCurrentView] = useState('back');
  const [itemCount, setItemCount] = useState(0);
  const [mainImageLoading, setMainImageLoading] = useState(true);
  const [thumbLoading, setThumbLoading] = useState({ front: true, back: true });

  useEffect(() => {
    if (tshirt && tshirt.colors && tshirt.colors.length > 0) {
      const initialColor = tshirt.initialColor || tshirt.colors[0].name;
      setSelectedColor(initialColor);
      if (productType === "hoodie") {
        setCurrentView(tshirt.name === "Overthinking" ? 'front' : 'front');
      } else {
        setCurrentView(tshirt.name === "Overthinking" ? 'front' : 'back');
      }
      setMainImageLoading(true);
    }
  }, [tshirt, productType]);

  useEffect(() => {
    if (selectedColor && selectedSize) {
      const count = cartItems.filter(item => 
        item.id === tshirt.id && 
        item.color === selectedColor && 
        item.size === selectedSize
      ).reduce((sum, item) => sum + item.quantity, 0);
      setItemCount(count);
    } else {
      setItemCount(0);
    }
  }, [cartItems, selectedColor, selectedSize, tshirt]);

  if (!tshirt) return null;

  const selectedColorData = tshirt.colors.find(c => c.name === selectedColor) || tshirt.colors[0];
  const currentImage = currentView === 'front' ? selectedColorData.frontImage || selectedColorData.backImage || selectedColorData.frontImage : selectedColorData.backImage || selectedColorData.frontImage || selectedColorData.backImage;

  const handleAddToCart = () => {
    if (!selectedSize) return;
    const productToAdd = {
      ...tshirt,
      size: selectedSize,
      color: selectedColor,
      image: selectedColorData.frontImage || selectedColorData.backImage || selectedColorData.frontImage,
      type: productType
    };
    onAddToCart(productToAdd);
  };

  const handleColorChange = (colorName) => {
    setSelectedColor(colorName);
    if (productType === "hoodie") {
      setCurrentView(tshirt.name === "Overthinking" ? 'front' : 'front');
    } else {
      setCurrentView(tshirt.name === "Overthinking" ? 'front' : 'back');
    }
    setMainImageLoading(true);
    setThumbLoading({ front: true, back: true });
  };

  const getColorStyle = (colorName) => {
    const colorMap = {
      'Negro': 'bg-black',
      'Beige': 'bg-amber-100',
      'Blanco': 'bg-white border border-gray-300',
      'Gris': 'bg-gray-500', 
      'Azul': 'bg-blue-600',
      'Verde': 'bg-green-600',
      'Verde Militar': 'bg-green-800',
      'Blanco con Azul': 'bg-gradient-to-br from-white to-blue-500 border border-gray-300',
      'Blanco con Negro': 'bg-gradient-to-br from-white to-black border border-gray-300',
      'Negro con Blanco': 'bg-gradient-to-br from-black to-white border border-gray-300',
      'Negro con Azul': 'bg-gradient-to-br from-black to-blue-900',
      'Navy': 'bg-blue-900',
      'Chocolate': 'bg-amber-900'
    };
    return colorMap[colorName] || 'bg-gray-200';
  };

  const hoodieSizeGuideImage = "https://i.postimg.cc/FztLMQt3/TALLA-HOODIES.png";
  const tshirtSizeGuideImage = "https://i.postimg.cc/MG72776K/TALLAS-CAMISETAS.jpg";

  const handleMainImageLoad = () => {
    setMainImageLoading(false);
  };

  const handleThumbLoad = (view) => {
    setThumbLoading(prev => ({ ...prev, [view]: false }));
  };

  const sizes = ['S', 'M', 'L', 'XL'];

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white p-8 max-w-6xl w-full relative max-h-[90vh] overflow-y-auto rounded-lg shadow-xl">
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-black transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        
        <div className="flex flex-col md:flex-row gap-10">
          <div className="md:w-1/2">
            <div className="relative overflow-hidden">
              {mainImageLoading && <Loader />}
              <img 
                src={currentImage} 
                alt="Producto VELNO" 
                className={`w-full h-auto object-cover ${mainImageLoading ? 'hidden' : ''}`}
                style={{ maxHeight: '3000px', maxWidth: '2000px' }}
                onLoad={handleMainImageLoad}
                onError={handleMainImageLoad}
              />
            </div>
            <div className="flex mt-4 space-x-2">
              <button
                onClick={() => setCurrentView('front')}
                className={`w-16 h-16 overflow-hidden border ${currentView === 'front' ? 'border-black' : 'border-gray-200'}`}
              >
                {thumbLoading.front && <Loader className="h-full w-full" />}
                <img 
                  src={selectedColorData.frontImage || selectedColorData.backImage || selectedColorData.frontImage} 
                  alt="Vista frontal" 
                  className={`w-full h-full object-cover ${thumbLoading.front ? 'hidden' : ''}`}
                  onLoad={() => handleThumbLoad('front')}
                  onError={() => handleThumbLoad('front')}
                />
              </button>
              <button
                onClick={() => setCurrentView('back')}
                className={`w-16 h-16 overflow-hidden border ${currentView === 'back' ? 'border-black' : 'border-gray-200'}`}
              >
                {thumbLoading.back && <Loader className="h-full w-full" />}
                <img 
                  src={selectedColorData.backImage || selectedColorData.frontImage || selectedColorData.backImage} 
                  alt="Vista trasera" 
                  className={`w-full h-full object-cover ${thumbLoading.back ? 'hidden' : ''}`}
                  onLoad={() => handleThumbLoad('back')}
                  onError={() => handleThumbLoad('back')}
                />
              </button>
            </div>
          </div>
          
          <div className="md:w-1/2 space-y-6">
            <h2 className="text-3xl font-serif">{tshirt.name}</h2>
            <p className="text-xl font-medium">${tshirt.price.toFixed(2)} USD</p>
            <p className="text-gray-700">{tshirt.description}</p>
            
            <div className="pt-4 border-t border-gray-200">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-lg font-medium">Tallas</h3>
                <button 
                  onClick={() => setShowSizeGuide(!showSizeGuide)}
                  className="text-sm underline hover:text-gray-600"
                >
                  {showSizeGuide ? 'Ocultar guía' : 'Ver guía de tallas'}
                </button>
              </div>
              <div className="flex space-x-3 mb-4 flex-wrap">
                {sizes.map(size => (
                  <button 
                    key={size}
                    onClick={() => setSelectedSize(size)}
                    className={`w-10 h-10 flex items-center justify-center border-2 ${selectedSize === size ? 'bg-black text-white border-black' : 'border-gray-300 hover:bg-gray-100'} transition-colors rounded-md m-1`}
                  >
                    {size}
                  </button>
                ))}
              </div>
              {showSizeGuide && (
                <div className="mb-4 space-y-4">
                  {productType === "tshirt" ? (
                    <img 
                      src={tshirtSizeGuideImage} 
                      alt="Guía de tallas Camisetas" 
                      className="w-full h-auto"
                    />
                  ) : (
                    <img 
                      src={hoodieSizeGuideImage} 
                      alt="Guía de tallas Hoodies" 
                      className="w-full h-auto"
                    />
                  )}
                </div>
              )}
            </div>
            
            <div className="pt-4 border-t border-gray-200">
              <h3 className="text-lg font-medium mb-3">Colores</h3>
              <div className="flex flex-wrap gap-3">
                {tshirt.colors && tshirt.colors.map(color => (
                  <button 
                    key={color.name}
                    onClick={() => handleColorChange(color.name)}
                    className={`w-8 h-8 rounded-full ${getColorStyle(color.name)} ${selectedColor === color.name ? 'ring-2 ring-offset-2 ring-black' : ''} transition-all`}
                    title={color.name}
                  />
                ))}
              </div>
            </div>
            
            <button 
              onClick={handleAddToCart}
              disabled={!selectedSize || !selectedColor}
              className={`mt-6 w-full py-3 px-6 rounded-lg ${selectedSize && selectedColor ? 'bg-black text-white hover:bg-gray-800' : 'bg-gray-200 text-gray-500 cursor-not-allowed'} transition-colors font-medium`}
            >
              Añadir al carrito {itemCount > 0 && `(${itemCount})`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TshirtModal;